import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";
import Pagination from "../Pagination";

const money = (amount, currency = "USD") => `${currency} ${Number(amount || 0).toFixed(2)}`;
const formatDateTime = (date) => date ? new Date(date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "-";

const AdminBillingCustomers = () => {
  const perPage = 25;
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const query = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
  });

  if (appliedKeyword) {
    query.set("keyword", appliedKeyword);
  }

  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + "/admin/billing-customers?" + query.toString());
  const customers = response?.billing_customers?.data ?? [];
  const meta = response?.billing_customers?.meta ?? {};
  const stats = response?.billing_customers?.stats ?? {};

  useEffect(() => {
    setPage(1);
  }, [appliedKeyword]);

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedKeyword(keyword.trim());
  };

  const clearFilters = () => {
    setKeyword("");
    setAppliedKeyword("");
  };

  return (
    <AdminPageShell
      active="billing-customers"
      title="Billing Customers"
      text="Read-only Stripe-linked employer billing summary."
    >
      <div className="row">
        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
          <div className="ui-item">
            <div className="left"><i className="icon la la-building"></i></div>
            <div className="right">
              <h4>{stats.customers_count ?? 0}</h4>
              <p>Employers</p>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
          <div className="ui-item ui-green">
            <div className="left"><i className="icon la la-credit-card"></i></div>
            <div className="right">
              <h4>{stats.stripe_customers_count ?? 0}</h4>
              <p>Stripe Customers</p>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
          <div className="ui-item ui-yellow">
            <div className="left"><i className="icon la la-dollar-sign"></i></div>
            <div className="right">
              <h4>{money(stats.paid_total ?? 0)}</h4>
              <p>Total Spend</p>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
          <div className="ui-item ui-red">
            <div className="left"><i className="icon la la-receipt"></i></div>
            <div className="right">
              <h4>{stats.payments_count ?? 0}</h4>
              <p>Payments</p>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      <div className="ls-widget">
        <div className="widget-title">
          <h4>Customers</h4>
          <Link to="/admin-payments" className="theme-btn btn-style-three">View Payments</Link>
        </div>
        <form className="admin-filter-bar admin-billing-filter" onSubmit={applyFilters}>
          <div className="admin-filter-field">
            <label>Search customers</label>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="Name, email, or Stripe customer ID"
            />
          </div>
          <div className="admin-filter-actions">
            <button type="submit" className="theme-btn btn-style-one">Search</button>
            <button type="button" className="theme-btn btn-style-three" onClick={clearFilters}>Reset</button>
          </div>
        </form>
        <div className="widget-content">
          {!error && !isLoading && meta?.total > 0 && (
            <div className="admin-list-summary">Showing {customers.length} of {meta.total} billing customers</div>
          )}

          <div className="admin-billing-table">
            <div className="admin-billing-head">
              <span>Customer</span>
              <span>Email</span>
              <span>Plan</span>
              <span>Total spend</span>
              <span>Payments</span>
              <span>Credits</span>
              <span>Last payment</span>
              <span></span>
            </div>
            {customers.map((customer) => (
              <div className="admin-billing-row" key={customer.id}>
                <div>
                  <strong>{customer.name}</strong>
                  <span>{customer.stripe_customer_id ?? "No Stripe customer yet"}</span>
                </div>
                <span>{customer.email}</span>
                <span>{customer.current_plan}</span>
                <span>{money(customer.total_spend)}</span>
                <span>{customer.payments_count}</span>
                <span>{customer.available_job_posts} available / {customer.credits_used} used</span>
                <span>{formatDateTime(customer.last_payment_at)}</span>
                <Link to={`/admin-employers/${customer.id}`} className="theme-btn btn-style-three">View</Link>
              </div>
            ))}
          </div>

          {!error && !isLoading && customers.length === 0 && <div className="admin-empty-state">No billing customers found.</div>}
          {!error && !isLoading && <Pagination meta={meta} onPageChange={setPage} label="Billing customer pagination" />}
        </div>
      </div>
    </AdminPageShell>
  );
};

export default AdminBillingCustomers;
